import React, { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Breadcrumb from '../components/Breadcrumb';
import { handelTitle } from '../utils';

/**
 * 404 Not Found page component.
 *
 * Displays a user-friendly error page when a requested route cannot be found.
 * Includes navigation back to home and browser back functionality.
 * Styled with web3-style-guide tokens (pixel-card, pixel-btn, no gradients).
 *
 * @module -not-found
 * @category Routes
 * @returns {React.ReactElement} The rendered 404 page
 */
const NotFound = () => {
  const navigate = useNavigate();

  useEffect(() => {
    handelTitle('Page Not Found');
  }, []);

  return (
    <>
      <main className="main-content mt-1 border-radius-lg">
      <div className="container-fluid">
        {/* Breadcrumb */}
        <Breadcrumb text1="Error" text2="404" />

        {/* Error Content */}
        <div className="row min-vh-100 align-items-center justify-content-center" style={{ minHeight: '500px' }}>
          <div className="col-lg-8 col-md-10 text-center">
            <div className="pixel-card">
            {/* Error Icon/Title */}
            <div className="mb-4">
              <h1
                className="display-1 fw-bold"
                style={{
                  fontSize: '10rem',
                  lineHeight: 1,
                  letterSpacing: '-0.05em',
                  color: 'var(--color-text-heading)',
                }}
              >
                404
              </h1>
              <div className="position-relative d-inline-block mt-3">
                <h2 className="h1 mb-0" style={{ color: 'var(--color-text-heading)' }}>Page Not Found</h2>
                <div className="position-absolute bottom-0 start-0 w-100" style={{ height: '3px' }}>
                  <div
                    style={{ height: '100%', width: '120px', background: 'var(--color-accent-blue)' }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            <p
              className="mb-5"
              style={{
                fontSize: '1.1rem',
                maxWidth: '600px',
                margin: '0 auto 2rem',
                lineHeight: 1.6,
                color: 'var(--color-text-body)',
              }}
            >
              Oops! The page you're looking for seems to have vanished into the blockchain.
              <br /><br />
              It might have been moved, deleted, or you entered the wrong address.
            </p>

            {/* Action Buttons */}
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <button
                onClick={() => navigate({ to: '/' }).catch(() => {})}
                className="pixel-btn"
              >
                <i className="fas fa-home me-2"></i>
                Continue
              </button>

              <button
                onClick={() => window.history.back()}
                className="pixel-btn-secondary"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Go Back
              </button>
            </div>

            {/* Decorative Dots */}
            <div className="mt-5 position-relative" style={{ height: '80px' }}>
              <div className="position-absolute top-50 start-50 translate-middle">
                <div className="d-flex gap-3" style={{ transform: 'scale(1.5)' }}>
                  <div className="rounded-circle" style={{ width: '12px', height: '12px', background: 'var(--color-accent-blue)', opacity: 0.7, boxShadow: '0 0 10px var(--color-accent-blue)' }}></div>
                  <div className="rounded-circle" style={{ width: '12px', height: '12px', background: 'var(--color-accent-green)', opacity: 0.5, boxShadow: '0 0 10px var(--color-accent-green)' }}></div>
                  <div className="rounded-circle" style={{ width: '12px', height: '12px', background: 'var(--color-accent-blue-hover)', opacity: 0.8, boxShadow: '0 0 10px var(--color-accent-blue-hover)' }}></div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    </>
  );
};

export default NotFound;
